import { LabelWithMessage } from '@common/LabelWithMessage';
import { DEFAULT_TABLE_PAGE_SIZE_OPTIONS, FETCH_QUERIES_NAME } from '@common/constants';
import { FormCheckbox, FormFieldMethods, FormSelect, Grid } from '@gcl/react-core';
import { useEffect } from 'react';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { useQueryClient } from '@tanstack/react-query';
import { EUserProfilePreferenceType } from './userProfilePreferences.constants';
import { IUpdateUserProfilePreferenceRequest, IUserProfilePreferences } from './userProfilePreferences.models';
import { useUpdateUserPreferences } from './userProfilePreferences.queries';

interface ISettingUserPreferenceRowProps {
  item: IUserProfilePreferences;
  formFieldMethods: FormFieldMethods;
}
export const SettingUserPreferenceRow = ({ item, formFieldMethods }: ISettingUserPreferenceRowProps) => {
  const queryClient = useQueryClient();

  const value = formFieldMethods.watch(`${item.id}`);

  const {
    mutate: mutateUpdateUserPreferences,
    isLoading: isLoadingUpdateUserPreferences,
    isSuccess: isSuccessUpdateUserPreferences,
    isError: isErrorUpdateUserPreferences,
    error: ErrorUpdateUserPreferences,
    reset: resetUpdateUserPreferences,
  } = useUpdateUserPreferences();

  useEffect(() => {
    if (!isSuccessUpdateUserPreferences) return;
    queryClient.invalidateQueries([FETCH_QUERIES_NAME.USER_PROFILE_PREFERENCES]);
  }, [isSuccessUpdateUserPreferences]);

  useEffect(() => {
    if (!formFieldMethods.formState.isDirty) return;
    if (value === null) return;

    let newValue = '';
    if (item.preference.attributeType === EUserProfilePreferenceType.BOOLEAN) {
      newValue = value ? 'true' : 'false';
    } else if (item.preference.attributeType === EUserProfilePreferenceType.PAGE_SIZE_DROPDOWN) {
      newValue = String(value.key);
    }

    const { preference, ...rest } = item;
    const request: IUpdateUserProfilePreferenceRequest = {
      ...rest,
      value: newValue,
    };
    mutateUpdateUserPreferences(request);
  }, [value]);

  let input = null;
  switch (item.preference.attributeType) {
    case EUserProfilePreferenceType.BOOLEAN:
      input = <FormCheckbox formFieldMethods={formFieldMethods} id={`${item.id}`} />;
      break;
    case EUserProfilePreferenceType.PAGE_SIZE_DROPDOWN:
      input = (
        <FormSelect formFieldMethods={formFieldMethods} id={`${item.id}`} options={DEFAULT_TABLE_PAGE_SIZE_OPTIONS} />
      );
      break;
    default:
      break;
  }
  return (
    <>
      <ApiErrorDisplay
        setFormError={formFieldMethods.setError}
        errors={[
          {
            error: ErrorUpdateUserPreferences,
            genericError: 'Update user preferences failed',
            reset: resetUpdateUserPreferences,
          },
        ]}
      />
      <Grid item xs={6} key={`${item.id}`}>
        <LabelWithMessage labelFor={`${item.id}`} text={item.preference.displayName ?? ''} />
        {input}
      </Grid>
    </>
  );
};
